import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Wallet, TrendingDown, Info } from "lucide-react";

const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

export default function OwnershipCostsCard({ vehicleId, className = "" }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("operating"); // operating | tco

  useEffect(() => {
    if (!vehicleId) return;
    api.get(`/reports/ownership-costs/${vehicleId}`).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [vehicleId]);

  if (!data) return null;
  const { operating, tco, km, months_owned, inputs } = data;
  const active = mode === "operating" ? operating : tco;
  const showTcoNeedsInput = mode === "tco" && !inputs.purchase_price && !inputs.insurance_annual;

  return (
    <div className={`border border-[color:var(--border-line,#262626)] bg-[color:var(--bg-surface,#121212)] ${className}`} data-testid="ownership-costs-card">
      <div className="px-5 py-3 border-b border-[color:var(--border-line,#262626)] flex items-center gap-2">
        <Wallet size={18} className="text-[color:var(--accent,#FF5A1F)]" />
        <span className="font-display text-lg font-black tracking-tight">Ownership Costs</span>
        <div className="ml-auto inline-flex border border-[color:var(--border-line,#262626)]">
          <button
            onClick={() => setMode("operating")}
            className={`px-3 py-1 text-xs font-semibold ${mode === "operating" ? "bg-[color:var(--accent,#FF5A1F)] text-[color:var(--accent-fg,#000)]" : "text-[color:var(--text-secondary,#A3A3A3)]"}`}
            data-testid="ownership-tab-operating"
          >
            OPERATING
          </button>
          <button
            onClick={() => setMode("tco")}
            className={`px-3 py-1 text-xs font-semibold ${mode === "tco" ? "bg-[color:var(--accent,#FF5A1F)] text-[color:var(--accent-fg,#000)]" : "text-[color:var(--text-secondary,#A3A3A3)]"}`}
            data-testid="ownership-tab-tco"
          >
            FULL TCO
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[color:var(--border-line,#262626)]">
        <div className="bg-[color:var(--bg-surface,#121212)] p-4">
          <div className="ft-overline text-[10px]">TOTAL</div>
          <div className="font-display text-3xl font-black tabular-nums">{inr(active.total)}</div>
        </div>
        <div className="bg-[color:var(--bg-surface,#121212)] p-4">
          <div className="ft-overline text-[10px]">₹ / KM</div>
          <div className="font-display text-3xl font-black tabular-nums">{active.cost_per_km ? inr(active.cost_per_km) : "—"}</div>
        </div>
        <div className="bg-[color:var(--bg-surface,#121212)] p-4">
          <div className="ft-overline text-[10px]">KM DRIVEN</div>
          <div className="font-display text-3xl font-black tabular-nums">{Math.round(km || 0).toLocaleString("en-IN")}</div>
        </div>
        <div className="bg-[color:var(--bg-surface,#121212)] p-4">
          <div className="ft-overline text-[10px]">MONTHS OWNED</div>
          <div className="font-display text-3xl font-black tabular-nums">{months_owned}</div>
        </div>
      </div>
      <div className="px-5 py-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        {mode === "operating" ? (
          <>
            <Row label="Fuel" value={inr(operating.fuel)} />
            <Row label="Charging" value={inr(operating.charging)} />
            <Row label="Service" value={inr(operating.service)} />
          </>
        ) : (
          <>
            <Row label="Operating" value={inr(tco.operating)} />
            <Row label="Insurance (amortised)" value={inr(tco.insurance_amortised)} />
            <Row label="Depreciation" value={inr(tco.depreciation)} />
          </>
        )}
      </div>
      {showTcoNeedsInput && (
        <div className="border-t border-[color:var(--border-line,#262626)] px-5 py-3 text-xs text-[color:var(--text-tertiary,#737373)] flex items-start gap-2">
          <Info size={12} className="mt-0.5 flex-shrink-0" />
          Add purchase price + insurance premium on the vehicle to unlock a complete TCO number.
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="ft-overline text-[10px]">{label}</div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}
